import React from 'react';
import { createPortal } from 'react-dom';
import { Lock, Sparkles, ArrowRight, X, Check } from 'lucide-react';
import { useSubscription } from '../context/SubscriptionContext';
import { useApp } from '../context/AppContext';
import { getColors, getModalStyle } from '../theme';
import { useNavigate } from 'react-router-dom';

/**
 * LockedFeature - Shows content but grayed out with upgrade overlay on hover
 * Use this to tease features to free users
 * 
 * @param {string} feature - Feature key from tiers.js
 * @param {ReactNode} children - Content to render
 * @param {function} onUpgradeClick - Custom upgrade handler (optional)
 * @param {string} upgradeText - Custom text for upgrade button (optional)
 * @param {boolean} showLockIcon - Show lock icon even when not hovered (optional)
 */
export const LockedFeature = ({ 
  feature, 
  children, 
  onUpgradeClick,
  upgradeText = 'Upgrade to Pro',
  showLockIcon = false,
}) => {
  const { hasFeature, tier } = useSubscription();
  const { isDark } = useApp();
  const colors = getColors(isDark);
  const navigate = useNavigate();
  
  const isLocked = !hasFeature(feature);

  // If not locked, just render children
  if (!isLocked) return children;

  const handleUpgrade = () => {
    if (onUpgradeClick) {
      onUpgradeClick();
    } else {
      navigate('/upgrade');
    }
  };

  return (
    <div className="relative group">
      {/* Render children but disabled/faded */}
      <div 
        style={{ 
          opacity: 0.4, 
          pointerEvents: 'none', 
          filter: 'grayscale(40%)',
          userSelect: 'none',
        }}
      >
        {children}
      </div>
      
      {/* Lock badge - always visible when showLockIcon */}
      {showLockIcon && (
        <div 
          className="absolute top-2 right-2 p-1.5 rounded-full"
          style={{ backgroundColor: `${colors.accent}20` }}
        >
          <Lock size={14} style={{ color: colors.accent }} />
        </div>
      )}
      
      {/* Upgrade overlay on hover */}
      <div 
        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 cursor-pointer rounded-md"
        style={{ backgroundColor: colors.bg }}
        onClick={handleUpgrade}
      >
        <div className="text-center p-4">
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
            style={{ backgroundColor: `${colors.accent}15` }}
          >
            <Sparkles size={24} style={{ color: colors.accent }} />
          </div>
          <p 
            className="text-sm font-medium mb-1"
            style={{ color: colors.textPrimary }}
          >
            Pro Feature
          </p>
          <p 
            className="text-xs mb-3"
            style={{ color: colors.textMuted }}
          >
            Unlock this with Pro
          </p>
          <button 
            className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 mx-auto transition-all "
            style={{ backgroundColor: colors.accent, color: colors.onAccent }}
          >
            {upgradeText}
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * FeatureGate - Conditionally renders content based on feature access
 * Use this to completely hide/show features
 * 
 * @param {string} feature - Feature key from tiers.js
 * @param {ReactNode} children - Content to render if has access
 * @param {ReactNode} fallback - Content to render if no access (optional)
 */
export const FeatureGate = ({ feature, children, fallback = null }) => {
  const { hasFeature } = useSubscription();
  
  if (hasFeature(feature)) return children;
  return fallback;
};

/**
 * LimitGate - Shows content with limit warning or upgrade prompt
 * Use this for items with quantity limits
 * 
 * @param {string} limitType - Limit key (maxHabits, maxGoals, etc.)
 * @param {number} currentCount - Current number of items
 * @param {ReactNode} children - Content to render
 * @param {function} onLimitReached - Handler when limit is reached
 */
export const LimitGate = ({ 
  limitType, 
  currentCount, 
  children, 
  onLimitReached,
}) => {
  const { canAddMore, getItemLimit, isPro } = useSubscription();
  const { isDark } = useApp();
  const colors = getColors(isDark);
  const navigate = useNavigate();
  
  const limit = getItemLimit(limitType);
  const canAdd = canAddMore(limitType, currentCount);
  const remaining = limit === Infinity ? '∞' : Math.max(0, limit - currentCount);

  const handleClick = (e) => {
    if (!canAdd) {
      e.preventDefault();
      e.stopPropagation();
      if (onLimitReached) {
        onLimitReached();
      } else {
        navigate('/upgrade');
      }
    }
  };

  return (
    <div onClick={handleClick}>
      {/* Show limit info for free users */}
      {!isPro && limit !== Infinity && (
        <div 
          className="text-xs mb-2 px-2 py-1 rounded inline-flex items-center gap-1"
          style={{ 
            backgroundColor: canAdd ? `${colors.accent}10` : `${colors.error}10`,
            color: canAdd ? colors.accent : colors.error,
          }}
        >
          {canAdd ? (
            <>{remaining} remaining</>
          ) : (
            <>
              <Lock size={10} />
              Limit reached - Upgrade for unlimited
            </>
          )}
        </div>
      )}
      
      <div style={{ opacity: canAdd ? 1 : 0.5, pointerEvents: canAdd ? 'auto' : 'none' }}>
        {children}
      </div>
    </div>
  );
};

/**
 * UpgradePrompt - Full page or section upgrade prompt
 * Use this as a fallback for gated pages
 * 
 * @param {string} feature - Feature name to display
 * @param {string} description - Description of the feature
 */
export const UpgradePrompt = ({ feature, description }) => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  const navigate = useNavigate();

  return (
    <div 
      className="flex flex-col items-center justify-center py-16 px-4 text-center"
      style={{ minHeight: '60vh' }}
    >
      <div 
        className="w-20 h-20 rounded-lg flex items-center justify-center mb-6"
        style={{ backgroundColor: `${colors.accent}15` }}
      >
        <Lock size={36} style={{ color: colors.accent }} />
      </div>
      
      <h2 
        className="text-2xl font-bold mb-2"
        style={{ color: colors.textPrimary }}
      >
        {feature} is a Pro Feature
      </h2>
      
      <p 
        className="text-base mb-6 max-w-md"
        style={{ color: colors.textMuted }}
      >
        {description || `Upgrade to Pro to unlock ${feature} and many other powerful features.`}
      </p>
      
      <button
        onClick={() => navigate('/upgrade')}
        className="px-6 py-3 rounded-md text-base font-medium flex items-center gap-2 transition-all "
        style={{ backgroundColor: colors.accent, color: colors.onAccent }}
      >
        <Sparkles size={18} />
        Upgrade to Pro
        <ArrowRight size={18} />
      </button>
      
      <p 
        className="text-sm mt-4"
        style={{ color: colors.textMuted }}
      >
        Starting at just $5/month
      </p>
    </div>
  );
};

/**
 * DevOnly - Only renders content for dev/admin users
 * 
 * @param {ReactNode} children - Content to render for devs
 */
export const DevOnly = ({ children }) => {
  const { isDev } = useSubscription();
  
  if (!isDev) return null;
  return children;
};

/**
 * DevBadge - Shows a dev/admin indicator
 */
export const DevBadge = () => {
  const { isDev } = useSubscription();
  const { isDark } = useApp();
  const colors = getColors(isDark);
  
  if (!isDev) return null;
  
  return (
    <span 
      className="px-2 py-0.5 rounded text-xs font-medium"
      style={{ 
        backgroundColor: `${colors.error}20`,
        color: colors.error,
      }}
    >
      DEV
    </span>
  );
};

/**
 * UpgradeLimitModal - Modal shown when user hits their tier limit
 * Shows current usage, limit, and value props for upgrading
 * 
 * @param {boolean} isOpen - Whether modal is visible
 * @param {function} onClose - Handler to close modal
 * @param {string} itemType - Type of item (habits, tasks, goals, events)
 * @param {number} currentCount - Current number of items
 * @param {number} limit - The limit for free tier
 */
export const UpgradeLimitModal = ({ 
  isOpen, 
  onClose, 
  itemType = 'items',
  currentCount = 0,
  limit = 5,
}) => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    onClose();
    navigate(`/upgrade?limit=${itemType}`);
  };

  const itemLabel = itemType.toLowerCase();
  const singularLabel = itemLabel.endsWith('s') ? itemLabel.slice(0, -1) : itemLabel;

  const valueProps = [
    { text: `Unlimited ${itemLabel}`, highlight: true },
    { text: 'Unlimited tasks, goals & events' },
    { text: 'Google Calendar sync' },
    { text: 'Priority support' },
  ];

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 animate-fade-in"
      onClick={onClose}
    >
      <div 
        className="w-full max-w-sm rounded-lg p-6 animate-scale-up"
        style={getModalStyle(colors)}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full transition-all hover:opacity-70"
          style={{ color: colors.textMuted }}
        >
          <X size={18} />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div 
            className="w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: `${colors.accent}15` }}
          >
            <Lock size={28} style={{ color: colors.accent }} />
          </div>
          
          <h3 
            className="text-xl mb-2 font-semibold tracking-tight"
            style={{ color: colors.textPrimary }}
          >
            Limit Reached
          </h3>
          
          <p 
            className="text-sm"
            style={{ color: colors.textMuted }}
          >
            You've created <span style={{ color: colors.accent, fontWeight: 600 }}>{currentCount}</span> of <span style={{ color: colors.accent, fontWeight: 600 }}>{limit}</span> {itemLabel}.
          </p>
        </div>

        {/* Value Props */}
        <div 
          className="rounded-md p-4 mb-6"
          style={{ backgroundColor: colors.bgTertiary }}
        >
          <p 
            className="text-xs mb-3 font-medium"
            style={{ color: colors.textMuted }}
          >
            Upgrade to Pro for:
          </p>
          <div className="space-y-2">
            {valueProps.map((prop, i) => (
              <div key={i} className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: prop.highlight ? colors.accent : `${colors.accent}30` }}
                >
                  <Check size={10} color={prop.highlight ? colors.onAccent : colors.accent} />
                </div>
                <span 
                  className="text-sm"
                  style={{ 
                    color: prop.highlight ? colors.textPrimary : colors.textSecondary,
                    fontWeight: prop.highlight ? 600 : 400,
                  }}
                >
                  {prop.text}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={handleUpgrade}
          className="w-full py-3 rounded-md text-sm font-medium flex items-center justify-center gap-2 transition-all "
          style={{ backgroundColor: colors.accent, color: colors.onAccent }}
        >
          <Sparkles size={16} />
          Upgrade to Pro: $5/mo
          <ArrowRight size={16} />
        </button>

        {/* Dismiss */}
        <button
          onClick={onClose}
          className="w-full mt-3 py-2 text-sm transition-opacity hover:opacity-70"
          style={{ color: colors.textMuted }}
        >
          Maybe later
        </button>
      </div>
    </div>,
    document.body
  );
};

export default LockedFeature;
